import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

const ADMIN_SECRET_STORAGE_KEY = 'evalpro_admin_secret';

/**
 * Pantalla oculta para correr la batería de aceptación del motor de decisión.
 * Acceso: /admin/decision-tests (requiere login + admin secret).
 */
export default function AdminDecisionTestsPage() {
  const { user, loading: authLoading } = useAuth();
  const [adminSecret, setAdminSecret] = useState(
    () => localStorage.getItem(ADMIN_SECRET_STORAGE_KEY) || ''
  );
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const runTests = async () => {
    if (!adminSecret) {
      setError('Ingresá el Admin Secret para ejecutar la batería.');
      return;
    }
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${apiBase}/api/admin/decision-acceptance-test`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Admin-Secret': adminSecret,
        },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      const data = await res.json();
      setReport(data);
      localStorage.setItem(ADMIN_SECRET_STORAGE_KEY, adminSecret);
    } catch (e) {
      setError(e.message || 'Error al ejecutar la batería');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && adminSecret) runTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6 pb-20" data-testid="admin-decision-tests">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-1 font-['Inter']">
              Internal · Hidden
            </p>
            <h1 className="text-2xl font-bold text-slate-800 font-['Inter'] flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-[#1F3A5F]" />
              Motor de decisión — Batería de aceptación
            </h1>
            <p className="text-sm text-slate-500 mt-1 font-['Inter']">
              5 casos clínicos canónicos que cubren las 5 ramas del árbol de decisión.
            </p>
          </div>
          <Button
            onClick={runTests}
            disabled={loading || !adminSecret}
            className="bg-[#1F3A5F] hover:bg-[#1F3A5F]/90"
            data-testid="btn-run-decision-tests"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Ejecutando…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Ejecutar batería
              </>
            )}
          </Button>
        </div>

        {/* Admin secret input */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-slate-700 font-['Inter']">Admin Secret</CardTitle>
            <CardDescription className="text-xs font-['Inter']">
              Requerido para ejecutar el endpoint <code className="text-[11px] bg-slate-100 px-1 rounded">/api/admin/decision-acceptance-test</code>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1">
                <Label htmlFor="admin-secret" className="text-xs">X-Admin-Secret</Label>
                <Input
                  id="admin-secret"
                  type="password"
                  value={adminSecret}
                  onChange={(e) => setAdminSecret(e.target.value)}
                  placeholder="evalpro-admin-…"
                  className="border-slate-200"
                  data-testid="input-admin-secret"
                />
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-600 mt-2 font-['Inter']" data-testid="decision-tests-error">
                {error}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        {report && (
          <Card
            className={cn(
              'border-2',
              report.summary.all_passed
                ? 'border-green-300 bg-green-50'
                : 'border-red-300 bg-red-50'
            )}
            data-testid="decision-tests-summary"
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                {report.summary.all_passed ? (
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                ) : (
                  <XCircle className="h-10 w-10 text-red-600" />
                )}
                <div>
                  <p className="text-xs uppercase tracking-wider opacity-70 font-['Inter']">Resultado global</p>
                  <h2
                    className={cn(
                      'text-2xl font-bold font-["Inter"]',
                      report.summary.all_passed ? 'text-green-800' : 'text-red-800'
                    )}
                  >
                    {report.summary.all_passed
                      ? 'TODOS LOS CASOS PASARON'
                      : `${report.summary.failed}/${report.summary.total} CASOS FALLARON`}
                  </h2>
                  <p className="text-sm font-['Inter'] mt-0.5">
                    {report.summary.passed} de {report.summary.total} casos OK
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cases */}
        {report && report.cases.map((c) => <CaseCard key={c.case_id} caseResult={c} />)}
      </div>
    </Layout>
  );
}

function CaseCard({ caseResult }) {
  const { case_id, name, description, passed, expected, actual, diff } = caseResult;
  return (
    <Card
      className={cn(
        'border-l-4',
        passed ? 'border-l-green-500 border-y-slate-200 border-r-slate-200' : 'border-l-red-500 border-y-slate-200 border-r-slate-200'
      )}
      data-testid={`decision-case-${case_id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge
                className={cn(
                  'text-xs font-bold',
                  passed ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'
                )}
              >
                {passed ? 'PASS' : 'FAIL'}
              </Badge>
              <span className="text-xs text-slate-400 font-mono">Caso {case_id}</span>
            </div>
            <CardTitle className="text-base text-slate-800 font-['Inter']">{name}</CardTitle>
            <p className="text-xs text-slate-500 mt-1 font-['Inter']">{description}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="text-sm font-['Inter'] space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Esperado</p>
            <p className="text-xs text-slate-700 mt-1">
              <span className="font-mono text-slate-500">estado:</span>{' '}
              <span className="font-semibold">{expected.estado}</span>
            </p>
            <p className="text-xs text-slate-700 mt-0.5">
              <span className="font-mono text-slate-500">headline contiene:</span>{' '}
              "{expected.headline_contains}"
            </p>
          </div>
          <div
            className={cn(
              'p-3 rounded-lg border',
              passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            )}
          >
            <p className="text-[10px] uppercase tracking-wider opacity-70 font-medium">Obtenido</p>
            <p className="text-xs mt-1">
              <span className="font-mono opacity-60">estado:</span>{' '}
              <span className="font-semibold">{actual?.estado || '—'}</span>
            </p>
            <p className="text-xs mt-0.5">
              <span className="font-mono opacity-60">etiqueta:</span>{' '}
              "{actual?.etiqueta || '—'}"
            </p>
          </div>
        </div>

        {actual?.motivos_decision && actual.motivos_decision.length > 0 && (
          <div className="text-xs">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1">
              Motivos del motor
            </p>
            <ul className="space-y-1">
              {actual.motivos_decision.map((m, i) => (
                <li key={i} className="flex gap-2 text-slate-600">
                  <span className="text-slate-400">•</span>
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!passed && diff && diff.length > 0 && (
          <div className="p-3 bg-red-100 rounded-lg border border-red-300" data-testid={`decision-case-${case_id}-diff`}>
            <p className="text-[10px] uppercase tracking-wider text-red-700 font-bold mb-1">
              Diff esperado vs obtenido
            </p>
            <ul className="space-y-1 text-xs text-red-800">
              {diff.map((d, i) => (
                <li key={i} className="flex gap-2">
                  <span className="font-mono">−</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
